import React, { useState, useEffect } from 'react'
import { shallow } from 'zustand/shallow'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import { SelectChangeEvent } from '@mui/material'

import { useGetLoginInfo } from '@multiversx/sdk-dapp/hooks/account'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions'

import addressToHex from '../../util/addressToHex'
import { useAuthState, useAuthDispatch } from '../../contexts/authContext'
import { useChainAndNetwork } from 'src/store/chainAndNetwork'
import { mvxNetworkProvidersType } from '../../util/types'
import useBlockchainAdapter from '../../hooks/useBlockchainAdapter'
import { getNftCollections } from '../../contexts/blockchainActionCreators'

import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { NftSetSpecialRole } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/nft/nftSetSpecialRole'

import ConnectMvxInfo from '../../components/ConnectMvxInfo'
import SectionTitle from '../../components/kit/SectionTitle'
import SettingTitle from '../../components/kit/SettingTitle'
import FormInputField from '../../components/kit/FormInputField'
import FormActionButton from '../../components/kit/FormActionButton'
import NftCollectionSelect from '../../components/NftCollectionSelect'

interface INftRoles {
  ESDTRoleNFTCreate?: string
  ESDTRoleNFTBurn?: string
  ESDTRoleNFTUpdateAttributes?: string
  ESDTRoleNFTAddURI?: string
  ESDTTransferRole?: string
}

const defaultNftRoles: INftRoles = {
  ESDTRoleNFTCreate: 'ESDTRoleNFTCreate',
  ESDTRoleNFTBurn: 'ESDTRoleNFTBurn',
  ESDTRoleNFTUpdateAttributes: 'ESDTRoleNFTUpdateAttributes',
  ESDTRoleNFTAddURI: 'ESDTRoleNFTAddURI',
  ESDTTransferRole: 'ESDTTransferRole',
}

interface INftRolesLabels {
  ESDTRoleNFTCreate: string
  ESDTRoleNFTBurn: string
  ESDTRoleNFTUpdateAttributes: string
  ESDTRoleNFTAddURI: string
  ESDTTransferRole: string
}

const nftRolesLabels: INftRolesLabels = {
  ESDTRoleNFTCreate: 'Create NFT',
  ESDTRoleNFTBurn: 'Burn NFT',
  ESDTRoleNFTUpdateAttributes: 'Update NFT Attributes',
  ESDTRoleNFTAddURI: 'Add NFT URI',
  ESDTTransferRole: 'Transfer NFT',
}

const EsdtForm = () => {
  const adapter = useBlockchainAdapter()
  const { network } = useChainAndNetwork(
    (state) => ({
      network: state.network,
    }),
    shallow,
  )

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const identifier = queryParams.get('tokenIdentifier')
  const roleAddress = queryParams.get('addressToAssignRole')

  const roleNftCreate = queryParams.get('ESDTRoleNFTCreate') || defaultNftRoles.ESDTRoleNFTCreate
  const roleNftBurn = queryParams.get('ESDTRoleNFTBurn') || defaultNftRoles.ESDTRoleNFTBurn
  const roleNftUpdateAttributes =
    queryParams.get('ESDTRoleNFTUpdateAttributes') || defaultNftRoles.ESDTRoleNFTUpdateAttributes
  const roleNftAddUri = queryParams.get('ESDTRoleNFTAddURI') || defaultNftRoles.ESDTRoleNFTAddURI
  const roleNftTransfer = queryParams.get('ESDTTransferRole') || defaultNftRoles.ESDTTransferRole

  const [address, setAddress] = useState<string>('')
  const [tokenIdentifier, setTokenIdentifier] = useState<string>(identifier ?? '')
  const [addressToAssignRole, setAddressToAssignRole] = useState<string>(roleAddress ?? '')
  const [nftRoles, setNftRoles] = useState<INftRoles>({
    ESDTRoleNFTCreate: roleNftCreate,
    ESDTRoleNFTBurn: roleNftBurn,
    ESDTRoleNFTUpdateAttributes: roleNftUpdateAttributes,
    ESDTRoleNFTAddURI: roleNftAddUri,
    ESDTTransferRole: roleNftTransfer,
  })
  const [disabled, setDisabled] = useState<boolean>(false)
  const [nftCollections, setNftCollections] = useState<any[]>([])

  let url = new URL(window.location.href)
  const user = useAuthState().user
  const dispatchAction = useAuthDispatch()

  const initializer = {
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  }
  const zoid = new ZoidCore(initializer)

  const mvxNetworkProviders: mvxNetworkProvidersType = {
    testnet: 'https://devnet-api.multiversx.com',
    mainnet: 'https://api.multiversx.com',
  }

  const { isLoggedIn } = useGetLoginInfo()
  const navigate = useNavigate()

  useEffect(() => {
    const setAddressAfterLogin = async () => {
      const walletAddress = await adapter.multiversx?.getAddress()
      setAddress(walletAddress)
    }
    if (isLoggedIn && adapter.multiversx) {
      setAddressAfterLogin()
    }
  }, [isLoggedIn, adapter.multiversx])

  const mvxNftRolePayload: NftSetSpecialRole = {
    address,
    tokenIdentifier,
    addressToAssignRole: addressToAssignRole.length === 62 ? addressToHex(addressToAssignRole) : '',
    networkProvider: mvxNetworkProviders[network],
    rolesToAssign: nftRoles,
  }

  const handleTokenIdentifierChange = (e: SelectChangeEvent<string>) => {
    url.searchParams.set('tokenIdentifier', e.target.value)
    window.history.pushState({}, '', url)
    setTokenIdentifier(e.target.value)
  }

  const handleRoleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('addressToAssignRole', e.target.value)
    window.history.pushState({}, '', url)
    setAddressToAssignRole(e.target.value)
  }

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    property: keyof INftRoles,
  ) => {
    const { checked } = event.target
    // Update the object based on the checkbox state
    if (checked) {
      url.searchParams.set(property, property)
      window.history.pushState({}, '', url)
      setNftRoles((prevState) => ({
        ...prevState,
        [property]: property,
      }))
    } else {
      url.searchParams.delete(property)
      window.history.pushState({}, '', url)
      setNftRoles((prevState) => {
        const newState = Object.assign({}, prevState)

        delete newState[property]

        return newState
      })
    }
  }

  const handleSetOwnAddress = () => {
    setAddressToAssignRole(address || '')
  }

  const sessionPayload = sessionStorage.getItem('mvxNftRolePayload')
  const sessionPayloadParsed = JSON.parse(sessionPayload || '{}')

  const transactionStatus = useTrackTransactionStatus({
    transactionId: sessionPayloadParsed?.txId,
    onSuccess: async () => {
      setDisabled(false)
      setTimeout(() => {
        navigate(`/mint-nft?chain=MULTIVERSX&network=${network}&tokenIdentifier=${tokenIdentifier}`)
      }, 2100)
      setDisabled(false)
    },
    onCancelled: () => {
      setDisabled(false)
    },
    onFail() {
      toast.error('The transaction failed.', {
        progressClassName: 'simpleBar',
      })
      setDisabled(false)
    },
  })

  useEffect(() => {
    if (transactionStatus?.isPending) {
      setDisabled(true)
    }
  }, [transactionStatus?.isPending])

  useEffect(() => {
    dispatchAction({
      type: 'DISABLENETWORKOPTIONS',
      payload: disabled,
    })
  }, [disabled])

  const handleOpenCollectionSelect = async () => {
    if (address) {
      const collections = await getNftCollections(address, network)
      setNftCollections(collections)
    }
  }

  useEffect(() => {
    const fetchNftCollections = async () => {
      if (identifier && address) {
        const collections = await getNftCollections(address, network)
        setNftCollections(collections)
        setTokenIdentifier(identifier)
      }
    }
    fetchNftCollections()
  }, [identifier, address])

  const handleSetNftRoles = async () => {
    setDisabled(true)

    try {
      sessionStorage.setItem(
        'mvxNftRolePayload',
        JSON.stringify({
          ...mvxNftRolePayload,
          chain: 'multiversx',
          network,
          operation: 'NFT set roles',
        }),
      )
      const response = await zoid.multiversX.nftSetSpecialRole(mvxNftRolePayload)

      const { transaction, transactionData } = response?.data?.data || {}

      const setNftRoleTransaction = {
        data: transactionData.data,
        sender: transactionData.sender.bech32,
        // receiver: transactionData.receiver.bech32,
        receiver: 'erd1qqqqqqqqqqqqqqqpqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqzllls8a5w6u',
        value: transaction.value,
        gasLimit: transaction.gasLimit,
      }

      const transactionRes = await adapter.multiversx.sendTransaction({
        transactions: setNftRoleTransaction,
        transactionsDisplayInfo: {
          processingMessage: 'Processing Set NFT Roles transaction',
          errorMessage: 'An error has occurred during transaction',
          successMessage: 'Set NFT Roles transaction successful',
        },
        redirectAfterSign: false,
      })
      const payload = sessionStorage.getItem('mvxNftRolePayload')
      const parsedPayload = JSON.parse(payload as string)
      sessionStorage.setItem(
        'mvxNftRolePayload',
        JSON.stringify({
          ...parsedPayload,
          txId: transactionRes?.sessionId,
        }),
      )
    } catch (error) {
      console.error('error', error)
      setDisabled(false)
      if (error instanceof Error) {
        toast.error(error?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const renderNftRoles = () => {
    return Object.keys(defaultNftRoles).map((property) => {
      return (
        <Grid item xs={12} sm={5} md={3} lg={2} key={property}>
          <FormControlLabel
            label={nftRolesLabels[property as keyof INftRolesLabels]}
            sx={{ fontFamily: 'SpaceGrotesk, sans-serif' }}
            control={
              <Checkbox
                checked={Object.prototype.hasOwnProperty.call(nftRoles, property)}
                checkedIcon={<CheckBoxOutlinedIcon />}
                onChange={(e) => handleCheckboxChange(e, property as keyof INftRoles)}
                inputProps={{ 'aria-label': 'controlled' }}
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: '#2AEAFA',
                  },
                }}
              />
            }
          />
        </Grid>
      )
    })
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          width: '100%',
        }}>
        <ConnectMvxInfo infoText="You have to connect your wallet before setting a role." />
      </Box>
      <SectionTitle title="Settings" />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
        }}>
        <SettingTitle title="Symbol" alignCenter />
        <NftCollectionSelect
          nftCollections={nftCollections}
          selectedNftCollection={tokenIdentifier}
          handleNftCollectionChange={handleTokenIdentifierChange}
          onOpen={handleOpenCollectionSelect}
          sx={{
            '& .MuiFormLabel-root': {
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: 'SpaceGrotesk, sans-serif',
            },
            '.MuiSvgIcon-root': {
              '&.MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
            },
            '& .MuiSelect-outlined': {
              borderColor: 'transparent',
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.05)',
              fontFamily: 'SpaceGrotesk, sans-serif',
            },
          }}
        />
      </Box>
      {/* <BoxInputField
        title="Symbol"
        alignTitleCenter
        placeholder='e.g. "NCT-123abc"'
        value={tokenIdentifier}
        onChange={handleTokenIdentifierChange}
      /> */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
        }}>
        <SettingTitle title="Address to assign role" alignCenter />
        <Button
          variant="contained"
          color="success"
          onClick={handleSetOwnAddress}
          disabled={!address}
          sx={{
            fontWeight: '600',
            color: '#181D38',
            background: 'rgba(42, 234, 250, 0.7)',
            margin: '0 0 0 0',
            fontFamily: 'SpaceGrotesk, sans-serif',
            padding: '2px 6px',

            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.5)',
              background: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.7)',
            },
          }}>
          Set own address
        </Button>
        <FormInputField
          placeholder='e.g. "erd123..."'
          value={addressToAssignRole}
          onChange={handleRoleAddressChange}
        />
      </Box>
      {/* <BoxInputField
        title="Address to assign role"
        alignTitleCenter
        placeholder='e.g. "erd123..."'
        value={addressToAssignRole}
        onChange={handleRoleAddressChange}
      /> */}
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
          <SettingTitle title="NFT Roles" />
        </Grid>
        {renderNftRoles()}
      </Grid>
      <FormActionButton
        text="Set Roles"
        onClick={handleSetNftRoles}
        disabledState={disabled}
        disabled={disabled || !addressToAssignRole || !tokenIdentifier || !address}
      />
    </>
  )
}

export default EsdtForm
